/* L'armoire de Yasmina: shared "add to bag" for brand sections.
   - Adds through Shopify's AJAX cart and keeps Dawn's bag drawer + bubble in sync (with or without opening it).
   - Broadcasts the cart to free-shipping meters on every change, including quantity edits inside the drawer.
   - Fly-to-bag, heart burst and toast feedback, skipped when motion is off or reduced. */
(() => {
  if (window.ldyCommerce) return;

  const cfg = window.ldy || {};
  const strings = cfg.strings || {};
  const listeners = new Set();
  let lastCart = null;
  let refreshTimer;

  const reduced = () => !cfg.motion || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const drawer = () => document.querySelector('cart-drawer');
  const t = (key, vars = {}) =>
    Object.entries(vars).reduce((s, [k, v]) => s.replaceAll(`{{ ${k} }}`, v), strings[key] || key);

  /** Format cents with the shop's money format, e.g. "${{amount}}". */
  function formatMoney(cents) {
    const format = cfg.moneyFormat || '${{amount}}';
    const value = (Number(cents) || 0) / 100;
    // Guests shopping in another currency (Shopify Markets): the shop's money format would show the wrong symbol.
    const active = window.Shopify?.currency?.active;
    if (active && cfg.shopCurrency && active !== cfg.shopCurrency) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency: active,
        }).format(value);
      } catch (e) {
        console.error('[ldy] currency format', e);
      }
    }
    const fmt = (decimals, thousands, decimal) => {
      const [int, frac] = value.toFixed(decimals).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    };
    return format.replace(
      /\{\{\s*(\w+)\s*\}\}/,
      (_, kind) =>
        ({
          amount: fmt(2, ',', '.'),
          amount_no_decimals: fmt(0, ',', '.'),
          amount_with_comma_separator: fmt(2, '.', ','),
          amount_no_decimals_with_comma_separator: fmt(0, '.', ','),
          amount_with_apostrophe_separator: fmt(2, "'", '.'),
          amount_with_space_separator: fmt(2, ' ', ','),
        })[kind] || fmt(2, ',', '.'),
    );
  }

  /** Free-shipping progress for a cart, converting the shop-currency threshold to the guest's currency. */
  function freeShipping(cart) {
    const rate = Number(window.Shopify?.currency?.rate || 1);
    const goal = Math.round((cfg.freeShippingCents || 0) * rate);
    const total = cart ? cart.total_price : 0;
    return {
      enabled: Boolean(cfg.showMeter && goal > 0),
      goal,
      total,
      left: Math.max(goal - total, 0),
      progress: goal ? Math.min(total / goal, 1) : 0,
    };
  }

  function emit(cart) {
    lastCart = cart;
    listeners.forEach((fn) => fn(cart));
    document.dispatchEvent(new CustomEvent('ldy:cart', { detail: cart }));
  }

  async function fetchCart() {
    const res = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`Cart request failed (${res.status})`);
    return res.json();
  }

  function refreshCart() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(
      () =>
        fetchCart()
          .then(emit)
          .catch((e) => console.error('[ldy] cart refresh', e)),
      120,
    );
  }

  /** Subscribe to cart changes; called immediately with the latest known cart. */
  function onCart(fn) {
    listeners.add(fn);
    if (lastCart) fn(lastCart);
    else refreshCart();
    return () => listeners.delete(fn);
  }

  /** Re-render Dawn's drawer and bubble from a /cart/add response, without opening the drawer. */
  function paintSections(json) {
    const d = drawer();
    if (!d) {
      const bubble = document.getElementById('cart-icon-bubble');
      const html = json.sections?.['cart-icon-bubble'];
      if (bubble && html)
        bubble.innerHTML = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('.shopify-section').innerHTML;
      return;
    }
    d.querySelector('.drawer__inner')?.classList.remove('is-empty');
    d.classList.remove('is-empty');
    d.getSectionsToRender().forEach((section) => {
      const el = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const html = json.sections?.[section.id];
      if (el && html) el.innerHTML = d.getSectionInnerHTML(html, section.selector);
    });
    // The overlay lives inside the re-rendered markup; re-bind it like Dawn's renderContents does.
    d.querySelector('#CartDrawer-Overlay')?.addEventListener('click', d.close.bind(d));
  }

  /**
   * Add variants to the bag.
   * @param {{id:number, quantity?:number}[]} items
   * @param {{sourceImg?: HTMLImageElement, openDrawer?: boolean, title?: string, detail?: string, image?: string}} opts
   */
  async function add(items, opts = {}) {
    const d = drawer();
    const sections = d ? d.getSectionsToRender().map((s) => s.id) : ['cart-icon-bubble'];
    const res = await fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: items.map((i) => ({ id: i.id, quantity: i.quantity || 1 })),
        sections,
        sections_url: window.location.pathname,
      }),
    });
    const json = await res.json();
    if (!res.ok || json.status) {
      const message = json.description || json.message || t('add_error_generic');
      toast({ title: t('add_error_title'), sub: message, tone: 'error' });
      throw new Error(message);
    }

    // The bag is no longer empty: drop Dawn's empty-state class so the drawer header and focus trap work.
    d?.classList.remove('is-empty');
    const target = await fly(opts.sourceImg);
    if (opts.openDrawer && d) {
      d.setActiveElement(document.activeElement);
      d.renderContents(json);
    } else {
      paintSections(json);
      burst(target);
    }

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ldy-commerce', cartData: json });
    }

    const cart = await fetchCart();
    emit(cart);
    if (!opts.openDrawer) {
      const fs = freeShipping(cart);
      const shipping = !fs.enabled
        ? ''
        : fs.left > 0
          ? t('to_free_shipping', { amount: formatMoney(fs.left) })
          : t('free_shipping_unlocked');
      toast({
        image: opts.image,
        title: t('added', { title: opts.title || '' }),
        sub: [opts.detail, shipping].filter(Boolean).join('. '),
        action: t('view_bag'),
      });
    }
    return cart;
  }

  /* ---------- feedback ---------- */
  function bagTarget() {
    return [...document.querySelectorAll('#cart-icon-bubble, [data-ldy-bag-target]')].find(
      (el) => el.offsetParent !== null,
    );
  }

  function fly(img) {
    const target = bagTarget();
    if (!img || !target || reduced()) return Promise.resolve(target);
    const a = img.getBoundingClientRect();
    const b = target.getBoundingClientRect();
    const clone = img.cloneNode();
    clone.removeAttribute('id');
    clone.removeAttribute('srcset');
    clone.src = img.currentSrc || img.src;
    clone.className = 'ldy-fly';
    Object.assign(clone.style, {
      left: `${a.left}px`,
      top: `${a.top}px`,
      width: `${a.width}px`,
      height: `${a.height}px`,
    });
    document.body.appendChild(clone);
    const dx = b.left + b.width / 2 - (a.left + a.width / 2);
    const dy = b.top + b.height / 2 - (a.top + a.height / 2);
    return clone
      .animate(
        [
          { transform: 'translate(0,0) scale(1)', opacity: 1, borderRadius: '14px' },
          { transform: `translate(${dx * 0.55}px, ${dy * 0.35 - 60}px) scale(0.45)`, opacity: 1, offset: 0.55 },
          { transform: `translate(${dx}px, ${dy}px) scale(0.08)`, opacity: 0.6, borderRadius: '50%' },
        ],
        { duration: 720, easing: 'cubic-bezier(.5,0,.3,1)' },
      )
      .finished.then(() => {
        clone.remove();
        return target;
      });
  }

  const HEART = 'M10 17.5s-7.2-4.4-7.2-9.6A3.9 3.9 0 0 1 10 5.6a3.9 3.9 0 0 1 7.2 2.3c0 5.2-7.2 9.6-7.2 9.6z';
  function burst(target) {
    if (!target) return;
    target.classList.remove('ldy-bumped');
    void target.offsetWidth;
    target.classList.add('ldy-bumped');
    if (reduced()) return;
    const r = target.getBoundingClientRect();
    const box = document.createElement('div');
    box.className = 'ldy-burst';
    box.style.left = `${r.left + r.width / 2}px`;
    box.style.top = `${r.top + r.height / 2}px`;
    for (let i = 0; i < 6; i += 1) {
      const ang = (i / 6) * Math.PI * 2;
      const span = document.createElement('span');
      span.style.cssText = `--x:${Math.cos(ang) * 30}px;--y:${Math.sin(ang) * 30}px;--r:${i * 60}deg`;
      span.innerHTML = `<svg viewBox="0 0 20 20" width="12" height="12" aria-hidden="true"><path d="${HEART}" fill="${i % 2 ? '#f3b3a9' : '#e5685a'}"/></svg>`;
      box.appendChild(span);
    }
    document.body.appendChild(box);
    setTimeout(() => box.remove(), 800);
  }

  let toastEl;
  let toastTimer;
  /* The live region must exist before its first message, or screen readers miss it. */
  function ensureToast() {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'ldy-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
      toastEl.addEventListener('click', (e) => {
        if (!e.target.closest('[data-ldy-view-bag]')) return;
        toastEl.classList.remove('is-open');
        const d = drawer();
        if (d) d.open(e.target);
        else window.location.href = window.routes.cart_url;
      });
    }
    return toastEl;
  }

  function toast({ image, title, sub, action, tone } = {}) {
    ensureToast();
    toastEl.replaceChildren();
    toastEl.classList.toggle('is-error', tone === 'error');
    if (image) {
      const img = document.createElement('img');
      img.src = image;
      img.alt = '';
      img.width = 44;
      img.height = 44;
      toastEl.appendChild(img);
    }
    const p = document.createElement('p');
    p.textContent = title || '';
    if (sub) {
      const span = document.createElement('span');
      span.textContent = sub;
      p.append(document.createElement('br'), span);
    }
    toastEl.appendChild(p);
    if (action) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ldy-toast__action';
      btn.dataset.ldyViewBag = '';
      btn.textContent = action;
      toastEl.appendChild(btn);
    }
    requestAnimationFrame(() => toastEl.classList.add('is-open'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-open'), 3600);
  }

  /* Keep meters in sync with edits made inside Dawn's drawer or cart page. */
  document.addEventListener('DOMContentLoaded', () => {
    ensureToast();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source !== 'ldy-commerce') refreshCart();
      });
    }
  });

  /** Turn a stock note (.ldy-stock-note) into its friendly "that's all we have" warning, with a small nudge. */
  function stockWarn(note) {
    if (!note?.dataset.over) return;
    note.hidden = false;
    note.querySelector('[data-stock-text]').textContent = note.dataset.over;
    note.classList.add('is-over');
    if (reduced()) return;
    note.animate(
      [
        { transform: 'translateX(0)' },
        { transform: 'translateX(-4px)' },
        { transform: 'translateX(4px)' },
        { transform: 'translateX(0)' },
      ],
      { duration: 300 },
    );
  }

  window.ldyCommerce = { add, onCart, refreshCart, formatMoney, freeShipping, toast, fly, burst, stockWarn };
})();
